import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Tv, Radio, CalendarDays } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { Reveal, Overline } from "../components/motion";
import { WorkCard, SectionHead } from "../components/tvui";
import { useTranslate } from "../context/TranslateContext";
import { CHANNELS, SERIES } from "../mock/tv";

const CATS = ["Daily Serials", "Reality & Talent", "Comedy", "Talk Shows", "Children's", "Documentaries", "Classics", "News"];

export default function Television() {
  const { t } = useTranslate();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const broadcast = SERIES.filter((s) => s.releaseMedium !== "Streaming");
  const listenText = "Indian Television. Discover channels across every language — daily serials, reality and talent shows, comedy, talk shows, children's programmes and classics, with broadcast schedules where available.";

  return (
    <div className="min-h-screen bg-gradient-to-b from-paper to-surface">
      <InnerNav />
      <div className="mx-auto max-w-[1400px] px-6 py-14 md:px-12 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Overline>Television</Overline>
            <h1 className="mt-3 font-display text-5xl font-semibold tracking-tight text-cream md:text-7xl">{t("Indian ")}<span className="italic text-gradient-gold">{t("Television")}</span></h1>
            <p className="mt-4 font-body text-lg text-ash">{t("Channels, programmes and schedules across every Indian language — from prime-time serials to reality and classics.")}</p>
          </div>
          
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="tv-categories">
          {CATS.map((c) => <span key={c} className="rounded-full border border-edge px-4 py-2 font-body text-sm text-ash transition-colors hover:border-gold hover:text-gold">{t(c)}</span>)}
        </div>

        {/* Channels */}
        <div className="mt-16">
          <SectionHead overline="Channel Discovery" title={t("Channels")} icon={Radio} accent="#C0392B" />
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-6">
            {CHANNELS.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.04}>
                <Link to={`/channels/${c.slug}`} data-testid={`channel-tile-${c.slug}`}>
                  <motion.div whileHover={{ y: -6 }} className="group flex aspect-square flex-col items-center justify-center rounded-2xl border border-edge p-4 text-center transition-colors" style={{ background: c.accent + "1a" }}>
                    <div className="font-display text-3xl font-semibold" style={{ color: c.accent }}>{c.name.split(" ").map((w) => w[0]).join("")}</div>
                    <div className="mt-2 font-body text-sm text-cream">{c.name}</div>
                    <div className="font-body text-xs text-ash">{c.languages[0]}</div>
                  </motion.div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Programmes */}
        <div className="mt-16">
          <SectionHead overline="On Air & Popular" title={t("Programmes & Serials")} icon={Tv} accent="#E8873A"
            right={<Link to="/tv-serials" className="font-body text-sm text-gold hover:underline">{t("All serials")} →</Link>} />
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
            {broadcast.map((s, i) => <WorkCard key={s.slug} s={s} i={i} />)}
          </div>
        </div>

        <Link to="/release-calendar" data-testid="tv-calendar-cta" className="mt-12 inline-flex items-center gap-2 rounded-full border border-gold/50 px-6 py-3 font-body text-gold hover:bg-gold/10">
          <CalendarDays className="h-4 w-4" /> {t("See the release calendar")}
        </Link>
      </div>
    </div>
  );
}
